import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO, api } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";

export function AdminGate({ children }) {
  const { user, login, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // HARD RULE: the app domain (app.btmedu.co.za) is customer-only. The admin
  // console lives exclusively at btmedu.co.za/admin-login.
  const isAppDomain = window.location.hostname === "app.btmedu.co.za";
  if (isAppDomain) {
    return (
      <div className="min-h-screen bg-navy-dark text-white flex flex-col items-center justify-center px-6 text-center" data-testid="admin-blocked-app-domain">
        <span className="bg-cream rounded-2xl p-3 mb-6">
          <img src={LOGO} alt="BTMEDU" className="h-9 w-auto" />
        </span>
        <h1 className="font-display text-xl font-bold">Admin console isn't on the app address</h1>
        <p className="text-sm text-white/60 mt-2 max-w-xs">
          app.btmedu.co.za is the customer app. The admin console lives at its own address.
        </p>
        <a
          href="https://btmedu.co.za/admin-login"
          className="mt-6 h-12 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors inline-flex items-center"
          data-testid="admin-goto-admin-login"
        >
          Go to btmedu.co.za/admin-login
        </a>
      </div>
    );
  }

  // HARD RULE: the installed BTMEDU app (PWA) is customer-only. The admin
  // console — including its sign-in form — never renders inside it.
  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  if (isStandalone) {
    return (
      <div className="min-h-screen bg-navy-dark text-white flex flex-col items-center justify-center px-6 text-center" data-testid="admin-blocked-in-app">
        <span className="bg-cream rounded-2xl p-3 mb-6">
          <img src={LOGO} alt="BTMEDU" className="h-9 w-auto" />
        </span>
        <h1 className="font-display text-xl font-bold">Admin console isn't available in the app</h1>
        <p className="text-sm text-white/60 mt-2 max-w-xs">
          The BTMEDU app is for shopping only. The admin console lives at its own web address and opens in your browser.
        </p>
        <button
          onClick={() => window.open("/admin", "_blank", "noopener")}
          className="mt-6 h-12 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors"
          data-testid="admin-open-browser"
        >
          Open admin console in browser
        </button>
      </div>
    );
  }

  // While checking auth status on initial load, show a clean loading spinner
  if (user === null) {
    return (
      <div className="min-h-screen bg-navy-dark flex items-center justify-center" data-testid="admin-gate-loading">
        <div className="w-8 h-8 border-2 border-white/20 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Not logged in: render the admin sign-in form directly inline on /admin — NO REDIRECT
  if (user === false) {
    const submit = async (e) => {
      e.preventDefault();
      setBusy(true);
      setError("");
      try {
        const u = await login(form.email, form.password);
        if (u.role !== "admin" && u.role !== "staff") {
          setError("This account is not an administrator. Please sign in with an admin account.");
        }
      } catch (err) {
        const d = err?.response?.data?.detail;
        setError(typeof d === "string" ? d : "Invalid admin email or password");
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="min-h-screen bg-navy-dark flex flex-col items-center justify-center px-4 py-12" data-testid="admin-gate-login">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="bg-white rounded-2xl px-4 py-3 inline-flex">
              <img src={LOGO} alt="BTMEDU" className="h-10 w-auto" />
            </span>
            <h1 className="font-display text-2xl font-bold text-white mt-6">Admin Console</h1>
            <p className="text-sm text-white/50 mt-1" data-testid="admin-signin-label">
              Staff access only — sign in to manage the store
            </p>
          </div>
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-xs font-semibold text-red-600" data-testid="admin-gate-error">
                {error}
              </div>
            )}
            <form onSubmit={submit} className="space-y-4">
              <input
                required
                type="email"
                placeholder="Admin email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold"
                data-testid="admin-gate-email"
              />
              <PasswordInput
                required
                minLength={6}
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full h-12 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold"
                data-testid="admin-gate-password"
              />
              <button
                type="submit"
                disabled={busy}
                className="w-full h-12 rounded-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-white font-bold text-sm transition-colors gold-glow"
                data-testid="admin-gate-submit"
              >
                {busy ? "Signing in…" : "Sign in to admin console"}
              </button>
            </form>
            <div className="mt-4 text-center">
              {!forgotOpen ? (
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  className="text-xs font-semibold text-slate-400 hover:text-brand transition-colors"
                  data-testid="admin-forgot-link"
                >
                  Forgot your password?
                </button>
              ) : forgotSent ? (
                <p className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3" data-testid="admin-forgot-sent">
                  If that email has an account, a reset link is on its way — check your inbox (valid 1 hour).
                </p>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setBusy(true);
                    try {
                      await api.post("/auth/forgot-password", { email: forgotEmail });
                      setForgotSent(true);
                    } catch {
                      setError("Could not send the reset email — try again");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="flex gap-2"
                >
                  <input
                    required
                    type="email"
                    placeholder="Your admin email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-gold"
                    data-testid="admin-forgot-email"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="h-11 px-4 rounded-xl bg-navy text-white text-xs font-bold whitespace-nowrap disabled:opacity-50"
                    data-testid="admin-forgot-submit"
                  >
                    {busy ? "Sending…" : "Email reset link"}
                  </button>
                </form>
              )}
            </div>
          </div>
          <p className="text-center mt-6">
            <Link to="/" className="text-xs font-semibold text-white/40 hover:text-white/80 transition-colors" data-testid="admin-gate-store-link">
              ← Go to the BTMEDU store website instead
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Logged in as a customer: show inline screen on /admin to switch accounts — NO REDIRECT
  if (user.role !== "admin" && user.role !== "staff") {
    return (
      <div className="min-h-screen bg-navy-dark text-white flex flex-col items-center justify-center px-6 text-center" data-testid="admin-gate-wrong-account">
        <span className="bg-cream rounded-2xl p-3 mb-6">
          <img src={LOGO} alt="BTMEDU" className="h-9 w-auto" />
        </span>
        <h1 className="font-display text-xl font-bold">This is the admin console</h1>
        <p className="text-sm text-white/60 mt-2 max-w-sm">
          You're currently signed in as <span className="text-white font-semibold">{user.email}</span>, which is a
          customer account. Sign out and sign in with your admin account to manage the store.
        </p>
        <button
          onClick={async () => {
            await logout();
          }}
          className="mt-6 h-12 px-8 rounded-full bg-gold hover:bg-gold-hover text-white font-bold text-sm transition-colors"
          data-testid="admin-gate-switch-btn"
        >
          Sign in with admin account
        </button>
        <Link to="/" className="mt-4 text-xs font-semibold text-white/40 hover:text-white/80 transition-colors" data-testid="admin-gate-wrong-account-store">
          ← Go to the BTMEDU store website instead
        </Link>
      </div>
    );
  }

  // Verified admin: render the dashboard
  return children;
}
